import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import teamService from '../services/teamService';

const TeamContext = createContext();

export const useTeamContext = () => {
  const context = useContext(TeamContext);
  if (!context) {
    throw new Error('useTeamContext must be used within TeamProvider');
  }
  return context;
};

export const TeamProvider = ({ children }) => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const fetchingRef = useRef(false);

  // Fetch teams with pagination
  const fetchTeams = useCallback(async (pageNum = 1) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;

    if (pageNum === 1) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }
    setError(null);
    try {
      const response = await teamService.list({ page: pageNum, limit: 8 });
      if (response.status) {
        const newTeams = response.data.teams || [];
        const meta = response.data.meta;

        setTeams(prev => pageNum === 1 ? newTeams : [...prev, ...newTeams]);

        // Handle pagination state
        if (meta) {
          setHasMore(meta.current_page < meta.last_page);
          setPage(meta.current_page);
        } else {
          // No meta means backend returned everything in one shot — no more pages.
          setHasMore(false);
          setPage(pageNum);
        }
      } else {
        setError(response.message || 'Failed to fetch teams');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch teams');
    } finally {
      setLoading(false);
      setLoadingMore(false);
      setInitialized(true);
      fetchingRef.current = false;
    }
  }, []);

  // Add a new team to state (from API response after creation)
  const addTeam = useCallback((team) => {
    setTeams(prev => [team, ...prev]);
  }, []);

  // Update existing team in state (from API response after update)
  const updateTeam = useCallback((updatedTeam) => {
    setTeams(prev =>
      prev.map(team => team.id === updatedTeam.id ? updatedTeam : team)
    );
  }, []);

  // Remove team from state
  const removeTeam = useCallback((teamId) => {
    setTeams(prev => prev.filter(team => team.id !== teamId));
  }, []);

  // Find team by ID
  const getTeamById = useCallback((teamId) => {
    return teams.find(team => team.id === parseInt(teamId));
  }, [teams]);

  // Add team member to specific team (from API response)
  const addTeamMember = useCallback((teamId, member) => {
    setTeams(prev =>
      prev.map(team => {
        if (team.id === teamId) {
          return {
            ...team,
            members: [...(team.members || []), member],
            members_count: (team.members_count || 0) + 1
          };
        }
        return team;
      })
    );
  }, []);

  // Remove team member from specific team
  const removeTeamMember = useCallback((teamId, userId) => {
    setTeams(prev =>
      prev.map(team => {
        if (team.id === teamId) {
          return {
            ...team,
            members: (team.members || []).filter(m => m.id !== userId),
            members_count: Math.max((team.members_count || 0) - 1, 0)
          };
        }
        return team;
      })
    );
  }, []);

  // Update member role in specific team
  const updateTeamMemberRole = useCallback((teamId, userId, newRole) => {
    setTeams(prev =>
      prev.map(team => {
        if (team.id === teamId) {
          return {
            ...team,
            members: (team.members || []).map(m =>
              m.id === userId ? { ...m, role: newRole } : m
            )
          };
        }
        return team;
      })
    );
  }, []);

  // Clear all teams (on logout)
  const clearTeams = useCallback(() => {
    setTeams([]);
    setInitialized(false);
    setError(null);
  }, []);

  const value = {
    teams,
    loading,
    error,
    initialized,
    fetchTeams,
    addTeam,
    updateTeam,
    removeTeam,
    getTeamById,
    addTeamMember,
    removeTeamMember,
    updateTeamMemberRole,
    clearTeams,
    page,
    hasMore,
    loadingMore,
  };

  return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>;
};
